import { AppstoreAddOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TopologyNode, ZoneSummaryRow } from "@wms/domain";
import { Alert, Button, Card, Col, Empty, Row, Select, Table, Tabs, Tag, Tree, Typography } from "antd";
import type { DataNode } from "antd/es/tree";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuthStore } from "../auth/store";
import { EntityTable } from "../components/EntityTable";
import { GenerateLocationsModal } from "../components/GenerateLocationsModal";
import { PageHeader } from "../components/PageHeader";
import { useRefRows } from "../components/RefSelect";
import { WarehouseMap } from "../components/WarehouseMap";

function toDataNodes(nodes: TopologyNode[]): DataNode[] {
  return nodes.map((n) => ({
    key: n.key,
    title: `${n.code} ${n.name}${n.locations ? ` (${n.locations})` : ""}`,
    children: n.children.length ? toDataNodes(n.children) : undefined,
    isLeaf: n.kind === "rack",
  }));
}

function findNode(nodes: TopologyNode[], key: string): TopologyNode | undefined {
  for (const n of nodes) {
    if (n.key === key) return n;
    const inner = findNode(n.children, key);
    if (inner) return inner;
  }
  return undefined;
}

const FILTER_FIELD: Record<TopologyNode["kind"], string> = { warehouse: "warehouse_id", room: "room_id", aisle: "aisle_id", rack: "rack_id" };

/** Warehouse tree on the left, locations of the selected node on the right, generation wizard. */
export function TopologyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  // UXW-67: «Схема склада → Пандусы и ворота» — the docks card is one click away while the option is on
  const docksOn = useAuthStore((s) => s.option("use_docks"));
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  // BUG-02 / Astana report: the plan, the tree, the readiness table and the generator all follow one chosen warehouse
  const warehouses = useRefRows("warehouses");
  const [warehouseId, setWarehouseId] = useState<string | undefined>(() => {
    try {
      return localStorage.getItem("wms-topology-warehouse") ?? undefined;
    } catch {
      return undefined;
    }
  });
  const warehouseList = warehouses.data?.items ?? [];
  const currentWarehouse = warehouseList.find((w) => String(w.id) === warehouseId) ?? warehouseList[0];
  const effectiveWarehouseId = currentWarehouse ? String(currentWarehouse.id) : undefined;
  const chooseWarehouse = (id: string) => {
    setWarehouseId(id);
    setSelectedKey(null);
    try {
      localStorage.setItem("wms-topology-warehouse", id);
    } catch {
      /* ignore */
    }
  };
  const tree = useQuery({
    queryKey: ["topology", "tree", effectiveWarehouseId ?? "all"],
    queryFn: () => api.get<TopologyNode[]>("/topology/tree", { warehouse_id: effectiveWarehouseId }),
    enabled: !!effectiveWarehouseId,
  });
  // WMS-08 / REQ-12: readiness of the address grid per zone — created, free, occupied, reserved, blocked
  const summary = useQuery({
    queryKey: ["topology", "zones-summary", effectiveWarehouseId ?? "all"],
    queryFn: () => api.get<ZoneSummaryRow[]>("/topology/zones/summary", { warehouse_id: effectiveWarehouseId }),
    enabled: !!effectiveWarehouseId,
  });
  const totals = (summary.data ?? []).reduce(
    (acc, r) => ({ total: acc.total + r.total, free: acc.free + r.free, occupied: acc.occupied + r.occupied, reserved: acc.reserved + r.reserved, blocked: acc.blocked + r.blocked }),
    { total: 0, free: 0, occupied: 0, reserved: 0, blocked: 0 },
  );
  const emptyZones = (summary.data ?? []).filter((r) => r.zone_id && r.total === 0);

  const nodes = useMemo(() => toDataNodes(tree.data ?? []), [tree.data]);
  const selected = selectedKey && tree.data ? findNode(tree.data, selectedKey) : undefined;
  const baseFilter = selected ? { field: FILTER_FIELD[selected.kind], op: "eq", value: selected.id } : undefined;
  const canWrite = can("topology.locations.write");

  return (
    <div>
      <PageHeader
        title="Схема склада"
        subtitle={currentWarehouse ? `${currentWarehouse.code} — ${currentWarehouse.name}: план с ячейками, объёмный вид, дерево помещений и стеллажей` : "План с ячейками, объёмный вид, дерево помещений и стеллажей"}
        extra={
          <>
            <Select
              style={{ minWidth: 260 }}
              size="large"
              value={effectiveWarehouseId}
              onChange={chooseWarehouse}
              loading={warehouses.isLoading}
              options={warehouseList.map((w) => ({ value: String(w.id), label: `${w.code} — ${w.name}` }))}
              placeholder="Склад"
              aria-label="Склад"
            />
            <Button icon={<ReloadOutlined />} aria-label="Обновить" onClick={() => { queryClient.invalidateQueries({ queryKey: ["topology"] }); queryClient.invalidateQueries({ queryKey: ["locations"] }); }} />
            {can("topology.rooms.write") && (
              <Button icon={<PlusOutlined />} onClick={() => navigate(`/rooms/new${effectiveWarehouseId ? `?warehouse_id=${effectiveWarehouseId}` : ""}`)}>
                Помещение
              </Button>
            )}
            {can("topology.racks.write") && (
              <Button icon={<PlusOutlined />} onClick={() => navigate(`/racks/new${selected?.kind === "room" ? `?room_id=${selected.id}` : selected?.kind === "aisle" ? `?aisle_id=${selected.id}` : ""}`)}>
                Стеллаж
              </Button>
            )}
            {docksOn && can("topology.docks.read") && (
              <Button onClick={() => navigate("/docks")} data-testid="topology-docks">
                Пандусы и ворота
              </Button>
            )}
            {canWrite && (
              <Button type="primary" icon={<AppstoreAddOutlined />} onClick={() => setGenerateOpen(true)}>
                Сгенерировать ячейки
              </Button>
            )}
          </>
        }
      />
      <Tabs
        defaultActiveKey="map"
        items={[
          { key: "map", label: "План склада", children: <WarehouseMap key={effectiveWarehouseId ?? "default"} warehouseId={effectiveWarehouseId} /> },
          {
            key: "zones",
            label: "Готовность по зонам",
            children: (
              <Card size="small" title="Адреса по зонам: созданы, свободны, заняты, отложены, заблокированы" loading={summary.isLoading}>
                {emptyZones.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`Зоны без адресов: ${emptyZones.map((z) => `${z.code} ${z.name}`).join(", ")}`}
                    description="Создайте стеллажи в помещении зоны и сгенерируйте ячейки — иначе размещение и отбор в эту зону невозможны."
                  />
                )}
                <Table<ZoneSummaryRow>
                  size="small"
                  rowKey={(r) => r.zone_id ?? "none"}
                  pagination={false}
                  dataSource={summary.data ?? []}
                  columns={[
                    { title: "Зона", render: (_, r) => (r.zone_id ? <Link to={`/zones/${r.zone_id}`}>{r.code} — {r.name}</Link> : <Typography.Text type="secondary">{r.name}</Typography.Text>) },
                    { title: "Тип", dataIndex: "kind", width: 120, render: (v: string | null) => v ?? "—" },
                    { title: "Создано", dataIndex: "total", align: "right", width: 100, render: (v: number) => (v === 0 ? <Tag color="orange">нет адресов</Tag> : v) },
                    { title: "Свободно", dataIndex: "free", align: "right", width: 100, render: (v: number) => <Typography.Text style={{ color: "var(--wms-success, #389e0d)" }}>{v}</Typography.Text> },
                    { title: "Занято", dataIndex: "occupied", align: "right", width: 100 },
                    { title: "Отложено под заказы", dataIndex: "reserved", align: "right", width: 160 },
                    { title: "Заблокировано", dataIndex: "blocked", align: "right", width: 130, render: (v: number) => (v ? <Typography.Text type="danger">{v}</Typography.Text> : 0) },
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <b>Итого по складу</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><b>{totals.total}</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><b>{totals.free}</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right"><b>{totals.occupied}</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right"><b>{totals.reserved}</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right"><b>{totals.blocked}</b></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
                <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                  Свободно — пустая ячейка, куда можно класть; занято — есть остаток; отложено — часть остатка зарезервирована под заказы; заблокировано — ячейка неактивна или её состояние запрещает класть или брать.
                </Typography.Paragraph>
              </Card>
            ),
          },
          {
            key: "tree",
            label: "Дерево и список",
            children: (
              <Row gutter={16}>
                <Col xs={24} lg={7}>
                  <Card size="small" title="Склад → помещение → проезд → стеллаж" loading={tree.isLoading}>
                    {nodes.length ? (
                      <Tree
                        treeData={nodes}
                        defaultExpandAll
                        selectedKeys={selectedKey ? [selectedKey] : []}
                        onSelect={(keys) => setSelectedKey((keys[0] as string) ?? null)}
                      />
                    ) : (
                      <Empty description="Создайте помещение и стеллажи, затем сгенерируйте ячейки" />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={17}>
                  <Card size="small" title={selected ? `Места хранения: ${selected.code} ${selected.name}` : "Все места хранения"}>
                    <EntityTable
                      key={selectedKey ?? "all"}
                      entity="locations"
                      embedded
                      baseFilter={baseFilter}
                      hideFields={selected ? [FILTER_FIELD[selected.kind]] : []}
                      createParams={selected ? { [FILTER_FIELD[selected.kind]]: selected.id } : undefined}
                    />
                  </Card>
                </Col>
              </Row>
            ),
          },
        ]}
      />
      <GenerateLocationsModal
        open={generateOpen}
        warehouseId={effectiveWarehouseId}
        rackId={selected?.kind === "rack" ? selected.id : undefined}
        onClose={() => {
          setGenerateOpen(false);
          queryClient.invalidateQueries({ queryKey: ["topology"] });
        }}
      />
    </div>
  );
}
